import { useState } from "react";
import { Users } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import type { OperadorCatalogDto, OperadorFormValues } from "@/services/locacoes";
import { upperLocacaoText } from "./upperLocacaoText";

function emptyForm(): OperadorFormValues {
  return { nome: "", documento: "", telefone: "", observacao: "" };
}

function fromDto(o: OperadorCatalogDto): OperadorFormValues {
  return {
    nome: upperLocacaoText(o.nome),
    documento: o.documento,
    telefone: o.telefone,
    observacao: upperLocacaoText(o.observacao),
  };
}

interface Props {
  open: boolean;
  editing: OperadorCatalogDto | null;
  busy?: boolean;
  onClose: () => void;
  onSave: (values: OperadorFormValues, editingId: string | null) => Promise<void>;
  onDelete?: () => void;
  onMutationComplete?: () => void | Promise<void>;
}

export function ModalOperador({ open, editing, busy, onClose, onSave, onDelete, onMutationComplete }: Props) {
  const [values, setValues] = useState<OperadorFormValues>(emptyForm());
  const [error, setError] = useState<string | null>(null);

  useModalFormSession(open, editing?.id ?? "new", () => {
    setValues(editing ? fromDto(editing) : emptyForm());
    setError(null);
  });

  const handleSubmit = async () => {
    if (!values.nome.trim()) {
      setError("Informe o nome do operador.");
      return;
    }
    setError(null);
    try {
      await onSave(
        {
          nome: values.nome.trim(),
          documento: values.documento,
          telefone: values.telefone,
          observacao: values.observacao,
        },
        editing?.id ?? null,
      );
      await onMutationComplete?.();
      onClose();
    } catch {
      /* erro tratado no pai */
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-brand-600" />
          <span>{editing ? "Editar operador" : "Novo operador"}</span>
        </div>
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {editing && onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete} disabled={busy} className="text-red-600 hover:text-red-700">
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void handleSubmit()} loading={busy} disabled={!values.nome.trim()}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Input label="Nome *" uppercase value={values.nome} onChange={(e) => setValues((v) => ({ ...v, nome: e.target.value }))} />
        <Input
          label="Documento"
          value={values.documento}
          onChange={(e) => setValues((v) => ({ ...v, documento: e.target.value }))}
        />
        <Input
          label="Telefone"
          value={values.telefone}
          onChange={(e) => setValues((v) => ({ ...v, telefone: e.target.value }))}
        />
        <p className="text-xs text-slate-500">
          Para dar acesso ao app de campo, crie um usuário do tipo <strong>Operador</strong> em Acessos e vincule a este
          cadastro.
        </p>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Observação</label>
          <textarea
            value={values.observacao}
            onChange={(e) => setValues((v) => ({ ...v, observacao: upperLocacaoText(e.target.value) }))}
            rows={2}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>
      </div>
    </Modal>
  );
}
